import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import MetricTrendChart from "../charts/MetricTrendChart";
import { useDateTimeFormat } from "../../hooks/useDateTimeFormat";
import { getTertiaryColour } from "../../utils/getTertiaryColour";
import {
  buildRecordingPresentation,
  loadPatientRecordingDocument,
} from "./PatientRecordingOverlay";

function normaliseStatus(value) {
  if (typeof value !== "string") return "fail";

  const normalized = value.trim().toLowerCase();

  if (
    normalized === "pass" ||
    normalized === "passed" ||
    normalized === "good" ||
    normalized === "very good"
  ) {
    return "pass";
  }

  return "fail";
}

function statusToTrendScore(value) {
  return normaliseStatus(value) === "pass" ? 1 : 0;
}

function formatTrendStatusScore(value, t) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return t("profilePage.statusFail", "Fail");
  }

  if (numericValue > 0 && numericValue < 1) {
    return t("profilePage.measurementTrendPassRate", "{{percent}}% pass", {
      percent: Math.round(numericValue * 100),
    });
  }

  return Math.round(Number(value)) >= 1
    ? t("profilePage.statusPass", "Pass")
    : t("profilePage.statusFail", "Fail");
}

function getSortableTimestamp(value) {
  const ms = new Date(value || "").getTime();
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

function isFiniteTrendValue(value) {
  return typeof value === "number" && Number.isFinite(value);
}

const FAIL_POINT_COLOUR = "#F59E0B";

function getAverageValue(values) {
  if (!Array.isArray(values) || !values.length) {
    return null;
  }

  const sum = values.reduce((total, value) => total + value, 0);
  return sum / values.length;
}

const CHART_PRIORITY = {
  "metric:catScore": -2000,
  "metric:rr": -1000,
  "metric:meanExhaleInhaleAreaRatio": 800,
  "metric:meanRectangularAreaRatio": 810,
};

function getChartPriority(chart) {
  if (chart.kind === "quality") return 1000 + (chart.sortOrder || 0);
  if (Object.prototype.hasOwnProperty.call(CHART_PRIORITY, chart.key)) {
    return CHART_PRIORITY[chart.key];
  }
  return chart.sortOrder || 0;
}

function getTrendPointQuality(presentation) {
  const statusesByKey = new Map(
    (presentation?.statuses || [])
      .filter((status) => status?.key)
      .map((status) => [status.key, status.rawValue]),
  );

  const qualityValue =
    statusesByKey.get("signalQuality") ??
    statusesByKey.get("respiratoryQuality") ??
    statusesByKey.get("spirometryQuality") ??
    statusesByKey.get("cardiacQuality");

  if (qualityValue == null) {
    return null;
  }

  return normaliseStatus(qualityValue);
}

function buildCatTrendRecords(entries, formatDate, t) {
  if (!Array.isArray(entries) || !entries.length) {
    return [];
  }

  return entries
    .map((entry) => {
      const score = Number(entry?.score);
      if (!Number.isFinite(score)) {
        return null;
      }

      const xValue = getSortableTimestamp(entry?.takenAt);
      const dayLabel = formatDate(entry?.takenAt) || "--";

      return {
        dayKey:
          dayLabel === "--"
            ? `cat:${entry?.uid || "unknown"}:${entry?.name || xValue}`
            : `cat:${dayLabel}`,
        dayLabel,
        xValue,
        pointQuality: null,
        presentation: {
          metrics: [
            {
              key: "catScore",
              label: t("profilePage.catScore", "CAT score"),
              rawValue: score,
              unit: null,
              trendDecimals: 0,
            },
          ],
        },
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.xValue - right.xValue);
}

function buildTrendCharts(records) {
  const chartMap = new Map();

  const ensureChart = (config) => {
    if (!chartMap.has(config.key)) {
      chartMap.set(config.key, {
        ...config,
        sortOrder: chartMap.size,
        dayBuckets: new Map(),
        recordingPoints: [],
      });
    }

    return chartMap.get(config.key);
  };

  const ensureDayBucket = (chart, record) => {
    if (!chart.dayBuckets.has(record.dayKey)) {
      chart.dayBuckets.set(record.dayKey, {
        label: record.dayLabel,
        xValue: record.xValue,
        values: [],
        latestValue: null,
      });
    }

    return chart.dayBuckets.get(record.dayKey);
  };

  const pushBucketValue = (chart, record, value) => {
    if (!isFiniteTrendValue(value)) return;

    const bucket = ensureDayBucket(chart, record);
    bucket.values.push(value);
    bucket.latestValue = value;
    bucket.xValue = record.xValue;
    chart.recordingPoints.push({
      x: record.xValue,
      y: value,
      quality: record.pointQuality,
    });
  };

  records.forEach((record) => {
    record.presentation?.statuses?.forEach((status) => {
      if (!status?.key) return;

      const chart = ensureChart({
        key: `status:${status.key}`,
        label: status.label,
        kind: "quality",
      });

      pushBucketValue(chart, record, statusToTrendScore(status.rawValue));
    });

    [
      ...(record.presentation?.metrics || []),
      ...(record.presentation?.trendMetrics || []),
    ].forEach((metric) => {
      if (!metric?.key) return;

      const chart = ensureChart({
        key: `metric:${metric.key}`,
        label: metric.label,
        kind: "numeric",
        unit: metric.unit || "",
        yDecimals: metric.trendDecimals ?? 0,
      });

      pushBucketValue(chart, record, metric.rawValue);
    });
  });

  return Array.from(chartMap.values())
    .map((chart) => {
      const orderedBuckets = Array.from(chart.dayBuckets.values()).sort(
        (left, right) => left.xValue - right.xValue,
      );

      if (!orderedBuckets.length) {
        return null;
      }

      return {
        ...chart,
        labels: orderedBuckets.map((bucket) => bucket.label),
        xValues: orderedBuckets.map((bucket) => bucket.xValue),
        values: orderedBuckets.map((bucket) => getAverageValue(bucket.values)),
        recordingPoints: chart.recordingPoints,
        minMax: orderedBuckets.map((bucket) => ({
          min: Math.min(...bucket.values),
          max: Math.max(...bucket.values),
        })),
        counts: orderedBuckets.map((bucket) => bucket.values.length),
        totalPoints: orderedBuckets.reduce(
          (sum, bucket) => sum + bucket.values.length,
          0,
        ),
      };
    })
    .filter(Boolean)
    .sort((left, right) => getChartPriority(left) - getChartPriority(right));
}

function buildSharedXDomain(records) {
  const xValues = records
    .map((record) => Number(record?.xValue))
    .filter((value) => Number.isFinite(value));

  if (!xValues.length) {
    return null;
  }

  return {
    min: Math.min(...xValues),
    max: Math.max(...xValues),
  };
}

function TrendChartCard({
  chart,
  t,
  formatTooltipTitle,
  formatTooltipLabel,
  xDomainOverride,
}) {
  const averageSeriesLabel = t(
    "profilePage.measurementTrendAverage",
    "Daily average",
  );
  const resultSeriesLabel = t("profilePage.measurementTrendResult", "Result");
  const recordingSeriesLabel = t(
    "profilePage.measurementTrendRecordings",
    "Recordings",
  );
  const neutralSeriesLabel =
    chart.key === "metric:catScore" ? resultSeriesLabel : recordingSeriesLabel;
  const passPointColour = getTertiaryColour();
  const finiteValueCount = chart.values.filter((value) =>
    Number.isFinite(value),
  ).length;
  const displayPointCount = chart.totalPoints || finiteValueCount;
  const extraDatasets = useMemo(() => {
    const points = Array.isArray(chart.recordingPoints)
      ? chart.recordingPoints
      : [];
    const passPoints = points.filter((point) => point.quality === "pass");
    const failPoints = points.filter((point) => point.quality === "fail");
    const neutralPoints = points.filter(
      (point) => point.quality !== "pass" && point.quality !== "fail",
    );

    const datasets = [];

    if (failPoints.length) {
      datasets.push({
        label: t("profilePage.statusFail", "Fail"),
        data: failPoints,
        color: FAIL_POINT_COLOUR,
        pointRadius: 4,
        pointHoverRadius: 5,
        borderWidth: 0,
        showLine: false,
        order: -20,
      });
    }

    if (passPoints.length) {
      datasets.push({
        label: t("profilePage.statusPass", "Pass"),
        data: passPoints,
        color: passPointColour,
        pointRadius: 4,
        pointHoverRadius: 5,
        borderWidth: 0,
        showLine: false,
        order: -10,
      });
    }

    if (neutralPoints.length) {
      datasets.push({
        label: neutralSeriesLabel,
        data: neutralPoints,
        color: passPointColour,
        pointRadius: 4,
        pointHoverRadius: 5,
        borderWidth: 0,
        showLine: false,
        order: -10,
      });
    }

    return datasets;
  }, [chart.recordingPoints, neutralSeriesLabel, passPointColour, t]);

  return (
    <section className="rounded-2xl border border-greyscale-1 bg-container p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-typography">
            {chart.label}
          </h3>
        </div>
        <div className="shrink-0 rounded-full bg-background px-3 py-1 text-xs font-medium text-greyscale-3">
          {t("profilePage.measurementTrendPoints", "{{count}} pts", {
            count: displayPointCount,
          })}
        </div>
      </div>

      <MetricTrendChart
        height={220}
        labels={chart.labels}
        values={chart.values}
        xValues={chart.xValues}
        xDomainOverride={xDomainOverride}
        minMax={chart.minMax}
        counts={chart.counts}
        extraDatasets={extraDatasets}
        trendLinePoints={chart.recordingPoints}
        xTickFormat="dd/MM"
        xMaxTicksLimit={12}
        lineLabel={averageSeriesLabel}
        yUnit={chart.kind === "quality" ? "" : chart.unit || ""}
        yDecimals={chart.kind === "quality" ? 0 : (chart.yDecimals ?? 0)}
        yMin={chart.kind === "quality" ? -0.1 : undefined}
        yMax={chart.kind === "quality" ? 1.1 : undefined}
        yTickStep={chart.kind === "quality" ? 1 : undefined}
        showMinMaxBars
        showTrendLine
        showLegend
        formatTooltipTitle={formatTooltipTitle}
        formatTooltipLabel={formatTooltipLabel}
        formatYValue={
          chart.kind === "quality"
            ? (value) => formatTrendStatusScore(value, t)
            : undefined
        }
      />
    </section>
  );
}

function LoadingProgress({ completed, total, t }) {
  const hasProgress = total > 0;
  const progressPercent = hasProgress
    ? Math.round((completed / total) * 100)
    : null;

  return (
    <div className="mx-auto w-full max-w-xl rounded-2xl border border-greyscale-1 bg-background px-5 py-6">
      <p className="text-base font-semibold text-typography">
        {t(
          "profilePage.loadingMeasurementTrends",
          "Loading measurement trends…",
        )}
      </p>

      {hasProgress ? (
        <>
          <div
            className="mt-4 h-3 overflow-hidden rounded-full bg-greyscale-1"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={completed}
            aria-valuetext={t(
              "profilePage.loadingMeasurementTrendsProgress",
              "Fetching recordings {{completed}} / {{total}}",
              { completed, total },
            )}
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-200"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="mt-3 flex items-center justify-between gap-4 text-sm text-greyscale-3">
            <span>
              {t(
                "profilePage.loadingMeasurementTrendsProgress",
                "Fetching recordings {{completed}} / {{total}}",
                { completed, total },
              )}
            </span>
            <span className="font-medium text-typography">
              {progressPercent}%
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default function PatientMeasurementTrendsOverlay({
  entries,
  catEntries,
  open,
  onClose,
}) {
  const { t } = useTranslation();
  const { formatDate, formatDateTime } = useDateTimeFormat();
  const [state, setState] = useState({
    loading: false,
    error: null,
    charts: [],
    xDomain: null,
    progressCompleted: 0,
    progressTotal: 0,
  });

  useEffect(() => {
    const measurementEntries = Array.isArray(entries) ? entries : [];
    const questionnaireCatEntries = Array.isArray(catEntries) ? catEntries : [];

    if (
      !open ||
      (!measurementEntries.length && !questionnaireCatEntries.length)
    ) {
      setState({
        loading: false,
        error: null,
        charts: [],
        xDomain: null,
        progressCompleted: 0,
        progressTotal: 0,
      });
      return;
    }

    let cancelled = false;

    const loadTrends = async () => {
      const sortedEntries = [...measurementEntries].sort(
        (left, right) =>
          getSortableTimestamp(left?.takenAt) -
          getSortableTimestamp(right?.takenAt),
      );

      setState({
        loading: true,
        error: null,
        charts: [],
        xDomain: null,
        progressCompleted: 0,
        progressTotal: sortedEntries.length,
      });

      try {
        const catTrendRecords = buildCatTrendRecords(
          questionnaireCatEntries,
          formatDate,
          t,
        );

        const results = await Promise.allSettled(
          sortedEntries.map(async (entry) => {
            try {
              const match = await loadPatientRecordingDocument(entry);
              const presentation = buildRecordingPresentation(
                { ...entry, variant: match.variant },
                match.data,
                t,
              );
              const xValue = getSortableTimestamp(entry?.takenAt);
              const dayLabel = formatDate(entry?.takenAt) || "--";

              return {
                entry,
                presentation,
                pointQuality: getTrendPointQuality(presentation),
                dayKey:
                  dayLabel === "--"
                    ? `${entry?.uid || "unknown"}:${entry?.name || xValue}`
                    : dayLabel,
                dayLabel,
                xValue,
              };
            } finally {
              if (cancelled) return;

              setState((previousState) => {
                if (!previousState.loading) {
                  return previousState;
                }

                return {
                  ...previousState,
                  progressCompleted: Math.min(
                    previousState.progressCompleted + 1,
                    previousState.progressTotal,
                  ),
                };
              });
            }
          }),
        );

        if (cancelled) return;

        const fulfilledResults = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);

        const trendRecords = [...fulfilledResults, ...catTrendRecords];

        setState({
          loading: false,
          error: null,
          charts: buildTrendCharts(trendRecords),
          xDomain: buildSharedXDomain(trendRecords),
          progressCompleted: sortedEntries.length,
          progressTotal: sortedEntries.length,
        });
      } catch (error) {
        if (cancelled) return;

        setState({
          loading: false,
          error,
          charts: [],
          xDomain: null,
          progressCompleted: 0,
          progressTotal: 0,
        });
      }
    };

    loadTrends();

    return () => {
      cancelled = true;
    };
  }, [catEntries, entries, formatDate, open, t]);

  const formatTooltipTitle = useMemo(() => () => "", []);

  const formatTooltipLabel = useMemo(
    () =>
      ({ datasetLabel, x, yFormatted, yUnit, label }) => {
        const dateTime = Number.isFinite(x)
          ? formatDateTime(x)
          : { date: label || "--", time: "" };
        const timestampLabel = [dateTime.date, dateTime.time]
          .filter(Boolean)
          .join(" ")
          .trim();
        const seriesLabel = datasetLabel ? ` (${datasetLabel})` : "";
        const prefix = `${timestampLabel || label || "--"}${seriesLabel}`;

        return yUnit
          ? `${prefix}: ${yFormatted} ${yUnit}`
          : `${prefix}: ${yFormatted}`;
      },
    [formatDateTime],
  );

  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label={t(
          "profilePage.closeMeasurementTrends",
          "Close measurement trends",
        )}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-greyscale-1 bg-container shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-greyscale-1 p-4">
          <div>
            <h2 className="text-2xl font-semibold text-typography">
              {t("profilePage.measurementTrendsTitle", "Measurement trends")}
            </h2>
            <p className="mt-1 text-sm text-greyscale-3">
              {t(
                "profilePage.measurementTrendsSubtitle",
                "Trend charts for recording metrics and CAT scores.",
              )}
            </p>
          </div>

          <button
            type="button"
            className="outlined-button !min-h-0 !px-4 !py-2 !text-sm"
            onClick={onClose}
          >
            {t("profilePage.closeOverlayButton", "Close")}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {state.loading ? (
            <div className="flex h-full items-center justify-center">
              <LoadingProgress
                completed={state.progressCompleted}
                total={state.progressTotal}
                t={t}
              />
            </div>
          ) : state.error ? (
            <div className="flex h-full items-center justify-center">
              <p className="max-w-md text-center text-red-500">
                {t(
                  "profilePage.measurementTrendsLoadError",
                  "We couldn't load the measurement trends. Please try again.",
                )}
              </p>
            </div>
          ) : !state.charts.length ? (
            <div className="flex h-full items-center justify-center">
              <p className="max-w-md text-center text-greyscale-3">
                {t(
                  "profilePage.noMeasurementTrends",
                  "No trend metrics are available for this patient yet.",
                )}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {state.charts.map((chart) => (
                <TrendChartCard
                  key={chart.key}
                  chart={chart}
                  t={t}
                  formatTooltipTitle={formatTooltipTitle}
                  formatTooltipLabel={formatTooltipLabel}
                  xDomainOverride={state.xDomain}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
